/**
 * Utility to extract field configurations from form API response
 * This eliminates the need for maintaining separate field configs
 */

// Form API types
export interface FormApiField {
  id: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: Array<{
    value: string;
    label: string;
  }>;
  api?: string;
  charLimit?: number | null;
}

export interface FormApiSection {
  heading: string;
  subheading?: string;
  columns?: number;
  fields: FormApiField[];
}

export interface FormApiType {
  dialogSize: string;
  sections: FormApiSection[];
}

export interface FormApiResponse {
  id: string;
  types: {
    [key: string]: FormApiType;
  };
}

// Overview field configuration extracted from form API
export interface OverviewFieldConfig {
  type: string;
  options?: string[];
  required?: boolean;
  label?: string;
  api?: string;
  charLimit?: number | null;
}

/**
 * Extracts overview section configuration from form API response
 */
export const extractOverviewConfig = (
  formApiResponse: FormApiResponse,
  momentType: string = 'Product Release with Marketing'
): Record<string, OverviewFieldConfig> => {
  const typeConfig = formApiResponse.types[momentType];
  if (!typeConfig) {
    console.warn(`Moment type "${momentType}" not found in form API response`);
    return {};
  }

  // Find the Overview section
  const overviewSection = typeConfig.sections.find(
    section => section.heading === 'Overview'
  );

  if (!overviewSection) {
    console.warn('Overview section not found in form API response');
    return {};
  }

  // Convert form API fields to overview field config
  const overviewConfig: Record<string, OverviewFieldConfig> = {};

  overviewSection.fields.forEach(field => {
    overviewConfig[field.name] = {
      type: field.type,
      options: field.options?.map(opt => opt.value) || [],
      required: field.required,
      label: field.label,
      api: field.api,
      charLimit: field.charLimit
    };
  });

  return overviewConfig;
};

/**
 * Maps form API field types to our internal field types for compatibility
 */
export const mapFormApiFieldType = (formApiType: string): string => {
  const typeMapping: Record<string, string> = {
    richtext: 'richtext',
    dropdown_richtext: 'multi_select_richtext', // Map to existing slideout type
    multi_select_richtext: 'multi_select_richtext',
    text: 'text',
    select: 'dropdown',
    datepicker: 'date',
    daterangepicker: 'daterange',
    api_dropdown: 'api_dropdown',
    multi_select_api: 'multi_select_api',
    ldap: 'ldap'
  };

  return typeMapping[formApiType] || formApiType;
};

/**
 * Creates a complete overview field configuration with mapped types
 */
export const createMappedOverviewConfig = (
  formApiResponse: FormApiResponse,
  momentType: string = 'Product Release with Marketing'
): Record<string, OverviewFieldConfig> => {
  const baseConfig = extractOverviewConfig(formApiResponse, momentType);

  // Map the field types to our internal types
  const mappedConfig: Record<string, OverviewFieldConfig> = {};

  Object.entries(baseConfig).forEach(([fieldName, config]) => {
    mappedConfig[fieldName] = {
      ...config,
      type: mapFormApiFieldType(config.type)
    };
  });

  return mappedConfig;
};

/**
 * Extracts dropdown options for a specific field from form API
 */
export const getFieldOptions = (
  formApiResponse: FormApiResponse,
  momentType: string,
  fieldName: string
): string[] => {
  const overviewConfig = extractOverviewConfig(formApiResponse, momentType);
  return overviewConfig[fieldName]?.options || [];
};

/**
 * Gets all available moment types from form API response
 */
export const getAvailableMomentTypes = (
  formApiResponse: FormApiResponse
): string[] => {
  return Object.keys(formApiResponse.types);
};

/**
 * Validates if a field exists in the form API configuration
 */
export const validateFieldExists = (
  formApiResponse: FormApiResponse,
  momentType: string,
  fieldName: string
): boolean => {
  const overviewConfig = extractOverviewConfig(formApiResponse, momentType);
  return fieldName in overviewConfig;
};

/**
 * Helper to get the form API field definition for a specific field
 */
export const getFormApiField = (
  formApiResponse: FormApiResponse,
  momentType: string,
  fieldName: string
): FormApiField | null => {
  const typeConfig = formApiResponse.types[momentType];
  if (!typeConfig) return null;

  const overviewSection = typeConfig.sections.find(
    section => section.heading === 'Overview'
  );

  if (!overviewSection) return null;

  return overviewSection.fields.find(field => field.name === fieldName) || null;
};
